// Screen-name navigation.
//
// Screens navigate by NAME ('patients'), never by URL literal. One map owns the
// name → path translation, so a route can be renamed in one place and so a nav
// entry for a screen that does not exist fails loudly here instead of rendering
// a dead link.
//
// tests/routing.test.js pins the three-way agreement: every key below has a
// matching <Route> in App.jsx, and every <Route> path is reachable through a key
// here. Adding a ROUTES entry without its <Route> fails the suite.

import { useNavigate } from 'react-router-dom';
import React from 'react';

export const ROUTES = {
  // امروز — the landing route and the clinic connection.
  today: () => '/',
  patients: () => '/patients',
  // `tab` is a URL SEGMENT, not local state: a refresh, a back button and a
  // shared link all have to restore the section the dentist was reading.
  // 'summary' is the default and is left out of the URL.
  patient: (d = {}) => `/patients/${d.id}${d.tab && d.tab !== 'summary' ? `/${d.tab}` : ''}`,
  prescription: (d = {}) =>
    `/prescription/${d.patientId}${d.appointmentId ? `?appointmentId=${encodeURIComponent(d.appointmentId)}` : ''}`,
  // جلسهٔ درمان — the visit workspace, keyed on the APPOINTMENT.
  //
  // The appointment is the visit (there is no encounters table), and it is the
  // only id in the path: the patient comes from the server's answer about this
  // appointment, so the URL cannot be edited to pair one patient's record with
  // another's visit.
  //
  // The left column's tab is a QUERY parameter rather than a path segment,
  // unlike the patient workspace's. The two are different kinds of thing: a
  // patient section is a place, and this is a pane of one workspace that stays
  // open around it. 'diagnosis' is the default and is left out.
  visit: (d = {}) =>
    `/visit/${d.appointmentId}${d.tab && d.tab !== 'diagnosis' ? `?tab=${encodeURIComponent(d.tab)}` : ''}`,
  profile: () => '/profile',
  // اعتبار — the Parsina Credit balance, ledger and top-up. A destination and
  // not a profile tab: it is where a dentist goes when the assistant answered
  // «اعتبار کافی نیست», and that has to be one tap away from anywhere.
  credits: () => '/credits',
};

export function useAppNavigate() {
  const navigate = useNavigate();

  return React.useCallback(
    (screen, data) => {
      const build = ROUTES[screen];
      if (!build) {
        // Loud rather than silent: a typo'd screen name is a dead button, and a
        // dead button is indistinguishable from a slow one.
        console.warn(`[useAppNavigate] no route for "${screen}"`);
        return;
      }
      navigate(build(data || {}), { state: data });
      window.scrollTo(0, 0);
    },
    [navigate],
  );
}

export default useAppNavigate;
